import { useState, useEffect } from "react";
import api, { formatAUD, formatErr } from "@/lib/api";
import { toast } from "sonner";
import SchematicPreview from "./SchematicPreview";
import {
  GitBranch, Copy, Send, Edit3, Eye, MessageSquare, Heart, Check,
  MessageCircleQuestion, Columns, X, Plus, Trash2,
} from "lucide-react";

const STATUS_LABEL = {
  draft: "Draft",
  released: "Released — current",
  feedback_received: "Feedback received",
  approved_direction: "Direction approved",
  superseded: "Previous version",
};

export default function ConceptVersionsPanel({ projectId, designVersions, onReload }) {
  const [editing, setEditing] = useState(null); // dv id
  const [compareOpen, setCompareOpen] = useState(false);
  const [viewingFb, setViewingFb] = useState(null);
  const [highlight, setHighlight] = useState(null);

  // Deep-link support: #dv-<id> scrolls + highlights the exact concept row.
  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/#dv-([\w-]+)/);
    if (!m || !designVersions || !designVersions.length) return;
    const dvid = m[1];
    setHighlight(dvid);
    // Wait for the row to render
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="concept-row-${dvid}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    // Also open feedback modal if the concept has any feedback
    const dv = designVersions.find((d) => d.id === dvid);
    if (dv && (dv.feedback || []).length > 0) setViewingFb(dv);
    return () => clearTimeout(timer);
  }, [designVersions]);

  const create = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/design-versions`, {});
      toast.success(`Concept V${data.version} created (draft)`);
      onReload?.();
      setEditing(data.id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const duplicate = async (dv) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/design-versions/${dv.id}/duplicate`);
      toast.success(`Concept V${data.version} created (duplicated from V${dv.version})`);
      onReload?.();
      setEditing(data.id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const release = async (dv) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/design-versions/${dv.id}/release`);
      if (data.email_status === "failed") {
        toast.warning(`Concept V${dv.version} released, but the client email could not be delivered. It has been logged.`);
      } else {
        toast.success(`Concept V${dv.version} released to client`);
      }
      onReload?.();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const items = designVersions || [];
  const drafts = items.filter((c) => c.status === "draft");
  const nextVersionNumber = (items[0]?.version || 0) + 1;

  return (
    <section className="mt-6" data-testid="concept-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline">Design Concepts</div>
          <h2 className="font-serif font-light mt-1" style={{ fontSize: "28px" }}>Concept Versioning</h2>
        </div>
        <div className="flex gap-2">
          {items.length >= 2 && (
            <button data-testid="btn-compare-concepts" onClick={() => setCompareOpen(true)} className="btn-outline text-xs">
              <Columns className="w-3.5 h-3.5" strokeWidth={1.5}/> Compare
            </button>
          )}
          <button data-testid="btn-create-concept" onClick={create} className="btn-primary text-xs">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5}/> Create Concept V{nextVersionNumber}
          </button>
        </div>
      </div>

      {items.length === 0 && (
        <div className="luxe-card p-8 text-center text-sm text-muted-foreground" data-testid="empty-concepts">
          No concepts yet. Create Concept V1 to snapshot the current schematic + palette for the client to review.
        </div>
      )}

      <div className="space-y-4">
        {items.map((dv) => (
          <ConceptRow
            key={dv.id}
            dv={dv}
            isEditing={editing === dv.id}
            isHighlighted={highlight === dv.id}
            onEditToggle={() => setEditing(editing === dv.id ? null : dv.id)}
            onRelease={() => release(dv)}
            onDuplicate={() => duplicate(dv)}
            onReload={onReload}
            onViewFeedback={() => setViewingFb(dv)}
            projectId={projectId}
          />
        ))}
      </div>

      {compareOpen && (
        <CompareModal items={items} onClose={() => setCompareOpen(false)}/>
      )}
      {viewingFb && (
        <FeedbackModal dv={viewingFb} onClose={() => setViewingFb(null)}/>
      )}

      {drafts.length > 1 && (
        <div className="text-xs mt-3" style={{ color: "#B85C38" }}>
          Note: you have multiple drafts. Only one can be released at a time — releasing a new concept supersedes any prior released version.
        </div>
      )}
    </section>
  );
}

function ConceptRow({ dv, isEditing, isHighlighted, onEditToggle, onRelease, onDuplicate, onReload, onViewFeedback, projectId }) {
  const [title, setTitle] = useState(dv.title || `Concept V${dv.version}`);
  const [desc, setDesc] = useState(dv.client_description || "");
  const [features, setFeatures] = useState(dv.key_features || []);
  const [showIndicative, setShowIndicative] = useState(dv.show_indicative_total !== false);
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [notes, setNotes] = useState(dv.description || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/design-versions/${dv.id}`, {
        title, client_description: desc, description: notes,
        key_features: features, show_indicative_total: showIndicative,
      });
      toast.success("Concept updated");
      onEditToggle();
      onReload?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const indicative = dv.indicative_total_cents != null ? dv.indicative_total_cents / 100 : null;
  const fbCounts = dv.feedback_counts || {};
  const totalFb = (fbCounts.love || 0) + (fbCounts.approve || 0) + (fbCounts.request || 0) + (fbCounts.comment || 0);
  const isDraft = dv.status === "draft";

  return (
    <div className={`luxe-card p-5 ${isHighlighted ? "ring-2 ring-[color:hsl(var(--accent))]" : ""}`}
         id={`dv-${dv.id}`} data-testid={`concept-row-${dv.id}`}>
      <div className="grid md:grid-cols-[180px,1fr] gap-5">
        <div>
          <SchematicPreview schematic={dv.schematic_snapshot} height={110}/>
          <div className="text-[10px] text-muted-foreground mt-2 text-center">Frozen elevation</div>
        </div>
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="overline text-[10px]">Version {dv.version} · {STATUS_LABEL[dv.status] || dv.status}</div>
              <div className="font-serif text-2xl mt-1">{dv.title || `Concept V${dv.version}`}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isDraft && (
                <>
                  <button data-testid={`btn-edit-${dv.id}`} onClick={onEditToggle} className="btn-outline text-xs"><Edit3 className="w-3 h-3" strokeWidth={1.5}/> {isEditing ? "Close" : "Edit"}</button>
                  <button data-testid={`btn-release-${dv.id}`} onClick={onRelease} className="btn-accent text-xs"><Send className="w-3 h-3" strokeWidth={1.5}/> Release</button>
                </>
              )}
              <button data-testid={`btn-duplicate-${dv.id}`} onClick={onDuplicate} className="btn-outline text-xs"><Copy className="w-3 h-3" strokeWidth={1.5}/> Duplicate</button>
              {totalFb > 0 && (
                <button data-testid={`btn-view-fb-${dv.id}`} onClick={onViewFeedback} className="btn-outline text-xs"><MessageSquare className="w-3 h-3" strokeWidth={1.5}/> Feedback ({totalFb})</button>
              )}
            </div>
          </div>

          {isEditing ? (
            <div className="mt-4 space-y-3">
              <div>
                <label className="overline text-[10px] block mb-1">Title (client-facing)</label>
                <input data-testid={`edit-title-${dv.id}`} className="luxe-input" value={title} onChange={(e) => setTitle(e.target.value)}/>
              </div>
              <div>
                <label className="overline text-[10px] block mb-1">Client description</label>
                <textarea data-testid={`edit-desc-${dv.id}`} rows={3} className="luxe-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Tell the story of the concept — what feeling, palette, hero moment."/>
              </div>
              <div>
                <label className="overline text-[10px] block mb-1">Key features</label>
                <div className="space-y-2">
                  {features.map((f, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input className="luxe-input flex-1" value={f} onChange={(e) => setFeatures(features.map((v, ix) => ix === i ? e.target.value : v))}/>
                      <button onClick={() => setFeatures(features.filter((_, ix) => ix !== i))} className="text-muted-foreground hover:text-[color:hsl(var(--destructive))] p-2"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input className="luxe-input flex-1" placeholder="Add a feature (e.g. Floating oak console)" value={newFeature} onChange={(e) => setNewFeature(e.target.value)}/>
                    <button data-testid={`btn-add-feature-${dv.id}`} onClick={() => { if (newFeature.trim()) { setFeatures([...features, newFeature.trim()]); setNewFeature(""); } }} className="btn-outline text-xs"><Plus className="w-3 h-3" strokeWidth={1.5}/></button>
                  </div>
                </div>
              </div>
              <div>
                <label className="overline text-[10px] block mb-1">Internal designer notes (never shown to client)</label>
                <textarea data-testid={`edit-notes-${dv.id}`} rows={2} className="luxe-input" value={notes} onChange={(e) => setNotes(e.target.value)}/>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid={`edit-indicative-${dv.id}`} type="checkbox" checked={showIndicative} onChange={(e) => setShowIndicative(e.target.checked)}/>
                Show indicative investment to client
                <span className="text-xs text-muted-foreground">({indicative != null ? formatAUD(indicative) : "—"} frozen at creation)</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onEditToggle} className="btn-outline text-xs">Cancel</button>
                <button data-testid={`btn-save-concept-${dv.id}`} onClick={save} disabled={saving} className="btn-primary text-xs">{saving ? "Saving…" : "Save concept"}</button>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm">
              {dv.client_description && <p className="text-muted-foreground line-clamp-2">{dv.client_description}</p>}
              <div className="text-xs text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1">
                <span>{(dv.line_items_snapshot || []).length} materials · {((dv.schematic_snapshot?.items || []).length)} elements</span>
                {indicative != null && <span>Indicative {formatAUD(indicative)} {dv.show_indicative_total === false && "(hidden from client)"}</span>}
                {dv.released_at && <span>Released {new Date(dv.released_at).toLocaleDateString("en-AU")}</span>}
                {dv.parent_version_id && <span>Duplicated concept</span>}
              </div>
              {totalFb > 0 && (
                <div className="text-xs mt-2 flex gap-3 text-muted-foreground" data-testid={`fb-counts-${dv.id}`}>
                  {fbCounts.love > 0 && <span><Heart className="w-3 h-3 inline" strokeWidth={1.5}/> {fbCounts.love}</span>}
                  {fbCounts.approve > 0 && <span><Check className="w-3 h-3 inline" strokeWidth={1.5}/> {fbCounts.approve}</span>}
                  {fbCounts.request > 0 && <span><MessageCircleQuestion className="w-3 h-3 inline" strokeWidth={1.5}/> {fbCounts.request}</span>}
                  {fbCounts.comment > 0 && <span><MessageSquare className="w-3 h-3 inline" strokeWidth={1.5}/> {fbCounts.comment}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CompareModal({ items, onClose }) {
  const [a, setA] = useState(items[0]?.id);
  const [b, setB] = useState(items[1]?.id);
  const A = items.find((i) => i.id === a);
  const B = items.find((i) => i.id === b);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="compare-modal">
      <div className="bg-card border border-border max-w-6xl w-full p-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Compare</div>
            <div className="font-serif text-3xl mt-1">Concept side-by-side</div>
          </div>
          <button onClick={onClose} className="p-2"><X className="w-5 h-5" strokeWidth={1.5}/></button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[[a, setA, A, "compare-a"], [b, setB, B, "compare-b"]].map(([val, setter, chosen, tid]) => (
            <div key={tid} className="border border-border p-4">
              <select value={val} onChange={(e) => setter(e.target.value)} className="luxe-input mb-3" data-testid={`select-${tid}`}>
                {items.map((it) => <option key={it.id} value={it.id}>V{it.version} — {it.title || `Concept V${it.version}`} ({STATUS_LABEL[it.status] || it.status})</option>)}
              </select>
              {chosen && (
                <div data-testid={`col-${tid}`}>
                  <SchematicPreview schematic={chosen.schematic_snapshot} height={200}/>
                  <div className="mt-3 space-y-2 text-sm">
                    <div><span className="overline text-[10px]">Title</span><div>{chosen.title}</div></div>
                    {chosen.client_description && <div><span className="overline text-[10px]">Description</span><div className="text-muted-foreground">{chosen.client_description}</div></div>}
                    <div><span className="overline text-[10px]">Materials</span>
                      <ul className="text-xs list-disc ml-4 mt-1">
                        {(chosen.line_items_snapshot || []).map((m) => <li key={m.id}>{m.name} — {m.quantity} {m.unit}</li>)}
                      </ul>
                    </div>
                    {chosen.indicative_total_cents != null && (
                      <div><span className="overline text-[10px]">Indicative investment</span><div className="font-serif text-xl">{formatAUD(chosen.indicative_total_cents / 100)}</div></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        {A && B && A.indicative_total_cents != null && B.indicative_total_cents != null && (
          <div className="mt-4 text-sm border-t border-border pt-4" data-testid="compare-delta">
            Indicative difference: <strong>{formatAUD((B.indicative_total_cents - A.indicative_total_cents) / 100)}</strong> ({B.indicative_total_cents >= A.indicative_total_cents ? "V" + B.version + " is higher" : "V" + B.version + " is lower"})
          </div>
        )}
      </div>
    </div>
  );
}

function FeedbackModal({ dv, onClose }) {
  const items = dv.feedback || [];
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="feedback-modal">
      <div className="bg-card border border-border max-w-2xl w-full p-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Client Feedback</div>
            <div className="font-serif text-2xl mt-1">Concept V{dv.version} — {dv.title}</div>
          </div>
          <button onClick={onClose} className="p-2"><X className="w-5 h-5" strokeWidth={1.5}/></button>
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No feedback yet.</div>
        ) : (
          <div className="space-y-3" data-testid="feedback-list">
            {items.map((f) => (
              <div key={f.id} className="border-l-2 pl-4 py-1" style={{ borderColor: iconColor(f.action) }}>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <span className="uppercase tracking-wider">{f.action}</span>
                  <span>·</span>
                  <span>{new Date(f.created_at).toLocaleString("en-AU")}</span>
                </div>
                {f.comment && <div className="mt-1 text-sm">{f.comment}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function iconColor(action) {
  return { love: "#B85C38", approve: "#3A4D39", request: "#A68A64", comment: "#8A8A8A" }[action] || "#5C5C5C";
}
